import { X } from 'lucide-react'
import { Button } from '@teach/ui'
import { cn } from '@/lib/utils'
import type { StudioJob } from '@/features/ai/studioTypes'
import { formatCreditsWithWord } from '@/features/ai/lib/money'
import { formatServerTime } from '@/features/ai/lib/serverTime'
import { translationImagesNotice } from '@/features/ai/lib/translationImagesNotice'
import type { StartFailure } from '@/features/ai/lib/parseStartError'
import {
  HEADER,
  outcomeOf,
  outcomeSkips,
  skipsAllFinal,
  showsImagePromptNote,
  countedPhrase,
  type Outcome,
  type Progress,
} from './runOutcomeView'
import { outcomeReason } from './emptyRunReason'
import { SkippedList } from './SkippedList'
import { TranslationImagesNotice } from './TranslationImagesNotice'
import { ResumeAction } from './ResumeAction'
import { useMessages } from '@/i18n'
import { messages } from './sidebarMessages'

interface Props {
  job: StudioJob
  /** Считает `useAiChat`: у него есть `entry.total` как fallback-знаменатель. */
  progress: Progress
  onDismiss: () => void
  /**
   * Три пропа кнопки продолжения — опциональные: итог рисуют ещё несколько
   * тестовых файлов и старые фикстуры этого же файла, у которых `job.resume`
   * не выставлен вовсе, а значит блок кнопки не рендерится и без них.
   * Обязательные в брифе, но переписывать десятки существующих вызовов ради
   * трёх нейтральных дефолтов было бы чистым шумом в диффе.
   */
  onResume?: () => void
  /** `reason: "already_resumed"` — «Показать продолжение» (§8, финальное
   *  ревью п.5), тот же опциональный проп по той же причине, что и три выше. */
  onShowResumed?: (jobId: number) => void
  isResuming?: boolean
  /** Отказ САМОЙ ручки продолжения (нажали и не вышло) — в отличие от
   *  `job.resume.reason`, который объясняет, почему кнопки нет. */
  resumeFailure?: StartFailure | null
  /** Курс на проверке — продолжение гасится (`ResumeAction`). */
  isFrozen?: boolean
}

const OutcomeHeader = ({ outcome, phrase }: { outcome: Outcome; phrase: string }) => {
  const { Icon, tone, title } = HEADER[outcome]
  return (
    <div className="flex items-center gap-2">
      <Icon className={cn('h-5 w-5 shrink-0', tone)} />
      <span className="font-medium text-foreground">{title(phrase)}</span>
    </div>
  )
}

export const RunOutcome = ({
  job,
  progress,
  onDismiss,
  onResume,
  onShowResumed,
  isResuming = false,
  resumeFailure = null,
  isFrozen,
}: Props) => {
  const m = useMessages(messages)
  const outcome = outcomeOf(job, progress)
  const phrase = countedPhrase(job.type, progress)
  const skips = outcomeSkips(job)
  const summary = job.result?.summary
  const credits = job.result?.credits_charged
  // §7.4, четвёртая ветка: перевод прошёл, а картинки второго языка не
  // поставлены. Молчаливый успех оставлял бы автора без картинок и без причины.
  const imagesNotice = translationImagesNotice(job)
  // Единственное место в студии, где автор вообще видит причину поломки.
  // Серверная у отказа, своя — у молчаливого нулевого прогона (сервер её на
  // картиночном пути не присылает, см. emptyRunReason).
  const reason = outcomeReason(job, outcome, skips.length > 0)

  return (
    <div
      data-testid="run-outcome"
      className={cn('m-3 space-y-2.5 rounded-2xl border p-4 text-sm', HEADER[outcome].surface)}
    >
      <div className="flex items-start justify-between gap-2">
        <OutcomeHeader outcome={outcome} phrase={phrase} />
        <Button variant="ghost" size="icon-sm" aria-label={m.close} onClick={onDismiss}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      {summary ? <p className="text-muted-foreground">{summary}</p> : null}
      {/* Нейтральный тон намеренно: исход объявляет заголовок (destructive
          либо warning), пояснение его не переспоривает. */}
      {reason ? (
        <p
          data-testid="run-outcome-error"
          className="whitespace-pre-line text-sm text-muted-foreground"
        >
          {reason}
        </p>
      ) : null}
      {skips.length > 0 ? <SkippedList items={skips} /> : null}
      {/* §9 + утверждённое отступление: `imagePrompt` — законная цель правки, но
          перерисовки в итоге нет (её механика в отдельном под-проекте), а без
          пояснения автор мог бы решить, что просьба «поменять картинку» ничего
          не сделала. */}
      {showsImagePromptNote(job, progress) ? (
        <p className="text-xs text-muted-foreground">{m.imagePromptNote}</p>
      ) : null}
      {imagesNotice ? (
        <TranslationImagesNotice message={imagesNotice} targetLang={job.target_lang} />
      ) : null}
      {credits ? (
        <p className="text-xs text-muted-foreground">{m.charged(formatCreditsWithWord(credits))}</p>
      ) : null}
      {/* Запись живёт в persist: итог недельной давности не должен читаться
          как «только что завершилось». */}
      {job.finished_at ? (
        <p data-testid="run-outcome-finished" className="text-xs text-muted-foreground">
          {m.finishedAt(formatServerTime(job.finished_at) ?? '—')}
        </p>
      ) : null}
      {/* Кнопку рисует ПОЛЕ `resume`, а не `status`: у прогона, которому
          нечего доделывать, кнопка обманула бы автора, а нажатие вернуло бы
          422. НЕ дописывать сюда `|| job.status === 'failed'` — доступность
          решает только сервер. */}
      {job.resume ? (
        <ResumeAction
          hint={job.resume}
          errorMessage={job.error_message}
          onResume={onResume}
          onShowResumed={onShowResumed}
          isResuming={isResuming}
          resumeFailure={resumeFailure}
          isFrozen={isFrozen}
          skipsAllFinal={skipsAllFinal(job, skips)}
        />
      ) : null}
    </div>
  )
}
